import { A } from "@solidjs/router";
import { HttpStatusCode } from "@solidjs/start";
import { SEO } from "~/components/SEO";
import styles from "./NotFound.module.css";
import { onMount, onCleanup, mergeProps } from "solid-js";
import { uiActions } from "~/stores/ui";
import { audioPlayerStore } from "~/stores/audioPlayer";

export interface NotFoundProps {
  type?: "page" | "article" | "host" | "programm" | "station" | "stream" | "podcast" | "episode";
  itemName?: string;
  fullPageMode?: boolean;
  /** Custom message to override the default generated message */
  customMessage?: string;
}

/**
 * Helper functions for NotFound titles and messages
 */
function getTitle(type: NotFoundProps["type"]) {
  switch (type) {
    case "article":
      return "Artikel nicht gefunden";
    case "programm":
      return "Programm nicht gefunden";
    case "host":
      return "Host nicht gefunden";
    case "station":
      return "Station nicht gefunden";
    case "stream":
      return "Stream nicht gefunden";
    case "podcast":
      return "Podcast nicht gefunden";
    case "episode":
      return "Episode nicht gefunden";
    default:
      return "Seite nicht gefunden";
  }
}

function getMessage(
  type: NotFoundProps["type"],
  itemName: string | undefined,
  customMessage: string | undefined
) {
  // Custom message takes precedence
  if (customMessage) {
    return customMessage;
  }

  switch (type) {
    case "article":
      return itemName
        ? `Der Artikel "${itemName}" wurde nicht gefunden.`
        : "Der angeforderte Artikel wurde nicht gefunden.";
    case "programm":
      return itemName
        ? `Das Programm "${itemName}" wurde nicht gefunden.`
        : "Das angeforderte Programm wurde nicht gefunden.";
    case "host":
      return itemName
        ? `Der Host "${itemName}" wurde nicht gefunden.`
        : "Der angeforderte Host wurde nicht gefunden.";
    case "station":
      return itemName
        ? `Die Station "${itemName}" wurde nicht gefunden.`
        : "Die angeforderte Station wurde nicht gefunden.";
    case "stream":
      return itemName
        ? `Der Stream "${itemName}" wurde nicht gefunden.`
        : "Der angeforderte Stream wurde nicht gefunden.";
    case "podcast":
      return itemName
        ? `Der Podcast "${itemName}" wurde nicht gefunden.`
        : "Der angeforderte Podcast wurde nicht gefunden.";
    case "episode":
      return itemName
        ? `Die Episode "${itemName}" wurde nicht gefunden.`
        : "Die angeforderte Episode wurde nicht gefunden.";
    default:
      return "Die angeforderte Seite existiert nicht oder wurde verschoben.";
  }
}

/**
 * NotFoundContent Component
 *
 * UI-only version of the 404 page WITHOUT SEO tags.
 * Use this as a fallback in <Show> components to avoid
 * overwriting the page's SEO during SSR.
 *
 * @example
 * ```tsx
 * <Show when={data()} fallback={<NotFoundContent type="article" />}>
 *   {(d) => <ArticleContent data={d()} />}
 * </Show>
 * ```
 */
export function NotFoundContent(rawProps: NotFoundProps = {}) {
  // Default fullPageMode logic:
  // - If 'type' is provided (specific error like station/article not found), default to TRUE (hide footer/margins)
  // - If 'type' is missing (generic 404 from router), default to FALSE (show footer/margins)
  const props = mergeProps({ fullPageMode: !!rawProps.type }, rawProps);

  onMount(() => {
    if (props.fullPageMode) {
      uiActions.setIsFooterVisible(false);
    }
  });

  onCleanup(() => {
    if (props.fullPageMode) {
      uiActions.setIsFooterVisible(true);
    }
  });

  return (
    <div
      class={styles.notFoundPage}
      classList={{
        "!mb-0": props.fullPageMode || !audioPlayerStore.currentStream,
      }}
    >
      {/* SSR-only: emits a real 404 for the catch-all route and every NotFoundContent
          fallback (PreviewPage, station/article not found). deferStream holds the first
          flush until data resolves, so the status lands before the headers go out. */}
      <HttpStatusCode code={404} />
      <div class={styles.contentWrapper}>
        <div class={styles.errorContainer}>
          <div class={styles.errorCode}>404</div>
          <h1 class={styles.errorTitle}>{getTitle(props.type)}</h1>
          <p class={styles.errorMessage}>
            {getMessage(props.type, props.itemName, props.customMessage)}
          </p>
          <div class={styles.actions}>
            <A href="/" class={styles.homeLink}>
              Zurück zur Startseite
            </A>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 404 Not Found Page
 *
 * Full version with SEO tags. Used as the actual 404 route.
 * For fallbacks in <Show> components, use NotFoundContent instead.
 */
export default function NotFound(props: NotFoundProps = {}) {
  return (
    <>
      <SEO
        title="404 - Seite nicht gefunden"
        description="Die angeforderte Seite wurde nicht gefunden. Zurück zur Absolut Radio Startseite."
        robots="noindex, follow"
      />
      <NotFoundContent {...props} />
    </>
  );
}
